import axios from 'axios'
import { deserialize } from 'deserialize-json-api'
import { cloneDeep } from 'lodash'
import uuidv4 from 'uuid/v4'
import router from '../../src/appRouter'

const PostEditor = {
  namespaced: true,
  state: {
    mode: null,
    wordsCount: 0,
    post: {
      id: null,
      title: '',
      introduction: '',
      isPagination: false,
      isIndex: false,
      isMoral: false,
      isPr: false,
      isPremiumDisplay: null,
      isTableOfContents: true,
      releasedOn: '',
      updatedAt: '',
      status: 'draft',
      eyecatchable_type: null,
      eyecatchable_id: null,
      eyecatch_source_url: null,
      littleTags: [],
      category: null,
      savedToDB: false,
      expertable: null,
      expertableId: null,
      expertableType: null,
      writerable: null,
      writerableId: null,
      writerableType: null,
      items: [],
      image_direct_url: '',
      slug: null,
      postType: null,
      pendingPublishReservation: null,
      product: {
        id: null,
        productLinks: []
      },
      regulationBundlerId: null,
      savedRegulationBundlerId: false,
      rankKeywords: {
        a: [],
        b: [],
        c: [],
        d: []
      }
    },
    posts: [],
    editingItemIndex: null,
    editingItemable: {},
    orderRegulationCheckResults: [],
    postRegulationCheckResults: [],
    toolBarStatus: 'edit',
    selectedCommentItem: null
  },
  mutations: {
    SET_MODE (state, data) {
      state.mode = data.mode
    },
    SET_WORDS_COUNT (state, wordsCount) {
      state.wordsCount = wordsCount
    },
    SET_ORDER_REGULATION_CHECK_RESULTS (state, results) {
      state.orderRegulationCheckResults = results
    },
    SET_POST_REGULATION_CHECK_RESULTS (state, results) {
      state.postRegulationCheckResults = results
    },
    SET_ORDER (state, data) {
      state.post.recentOrder = data.order
    },
    SET_INSPECTION (state, data) {
      state.post.recentOrder.currentActiveInspection = data.inspection
    },
    SET_POST (state, data) {
      state.post = data.post
    },
    SET_POST_SAVED_TO_DB (state, savedToDB) {
      state.post.savedToDB = savedToDB
    },
    SET_POSTS (state, posts) {
      state.posts = posts
    },
    SET_PUBLISH_RESERVATION (state, publishReservation) {
      Object.assign(state.post, {
        pendingPublishReservation: publishReservation
      })
    },
    DELETE_PUBLISH_RESERVATION (state) {
      Object.assign(state.post, { pendingPublishReservation: null })
    },
    UPDATE_POST_BASIC (state, payload) {
      Object.assign(state.post, payload)
    },
    UPDATE_CATEGORY (state, category) {
      state.post.category = category
    },
    UPDATE_LITTLE_TAGS (state, tags) {
      state.post.littleTags = tags
    },
    UPDATE_EXPERTABLE (state, expertable) {
      state.post.expertableId = expertable ? expertable.id : null
      state.post.expertableType = expertable ? expertable.type : null
    },
    UPDATE_WRITERABLE (state, writerable) {
      state.post.writerableId = writerable ? writerable.id : null
      state.post.writerableType = writerable ? writerable.type : null
    },
    UPDATE_REGULATION_BUNDLER (state, regulationBundlerId) {
      state.post.regulationBundlerId = regulationBundlerId
    },
    SET_SAVED_REGULATION_BUNDLER_ID (state, savedRegulationBundlerId) {
      state.post.savedRegulationBundlerId = savedRegulationBundlerId
    },
    UPDATE_RANK_KEYWORDS (state, rankKeywords) {
      state.post.rankKeywords = rankKeywords
    },
    UPDATE_ITEM (state, payload) {
      const item = state.post.items.find((item) => item.uuid === payload.uuid)
      Object.assign(payload, { isSaved: true })
      Object.assign(item, payload)
    },
    SET_TARGET_POST (state, payload) {
      const item = state.post.items.find((item) => item.uuid === payload.uuid)
      Object.assign(item.itemableAttributes, { targetPost: payload.post })
    },
    DELETE_ITEM (state, uuid) {
      const item = state.post.items.find((item) => item.uuid === uuid)
      if (item.id === null) {
        const index = state.post.items.findIndex((item) => item.uuid === uuid)
        state.post.items.splice(index, 1)
      } else {
        item._destroy = true
      }
      state.editingItemIndex = null
    },
    DELETE_ALL_ITEMS (state) {
      const excludeNewItemsArray = state.post.items.filter(
        (item) => item.id !== null
      )

      excludeNewItemsArray.forEach((item) => {
        item._destroy = true
      })

      state.post.items = excludeNewItemsArray
    },
    EDIT_ITEM (state, payload) {
      state.editingItemIndex = payload.index
      state.editingItemable = payload.item.itemableAttributes
    },
    UPDATE_EDITING_ITEMABLE (state, itemable) {
      state.editingItemable = itemable
    },
    END_EDIT_ITEM (state) {
      state.editingItemIndex = null
    },
    ADD_NEW_ITEM (state, payload) {
      const index = payload.index
      state.editingItemIndex = index
      const item = Object.assign(payload.item, {
        uuid: uuidv4(),
        isSaved: false
      })
      state.post.items.splice(index, 0, item)
      state.editingItemable = payload.item.itemableAttributes
    },
    SORT_ITEMS (state, items) {
      state.post.items = items
    },
    SET_TOOL_BAR_STATUS (state, status) {
      state.toolBarStatus = status
    },
    SET_SELECTED_COMMENT_ITEM (state, item) {
      state.selectedCommentItem = item
    },
    ADD_NEW_ITEM_COMMENT (state, { itemUuid, itemComment }) {
      const item = state.post.items.find((item) => item.uuid === itemUuid)
      Array.isArray(item.itemComments)
        ? item.itemComments.push(itemComment)
        : (item.itemComments = [itemComment])
    },
    UPDATE_ITEM_COMMENT (state, payload) {
      const itemIndex = state.post.items.findIndex(
        (item) => item.uuid === payload.itemUuid
      )
      const commentIndex = state.post.items[itemIndex].itemComments.findIndex(
        (comment) => comment.id === payload.itemComment.id
      )
      state.post.items[itemIndex].itemComments[commentIndex] =
        payload.itemComment
    },
    DELETE_ITEM_COMMENT (state, { itemUuid, itemCommentId }) {
      const commentIndex = state.post.items
        .find((item) => item.uuid === itemUuid)
        .itemComments.findIndex((comment) => comment.id === itemCommentId)
      state.post.items
        .find((item) => item.uuid === itemUuid)
        .itemComments.splice(commentIndex, 1)
    },
    ADD_ITEM_TO_END (state, payload) {
      const item = Object.assign(payload, {
        uuid: uuidv4(),
        isSaved: true
      })
      state.post.items.push(item)
      state.post.savedToDB = false
    }
  },
  getters: {
    activeItems (state) {
      return state.post.items.filter((item) => !item._destroy)
    },
    deletedItems (state) {
      return state.post.items.filter((item) => item._destroy)
    },
    recentOrder (state) {
      return state.post.recentOrder
    },
    currentActiveInspection (state) {
      return state.post.recentOrder.currentActiveInspection
    },
    completedInspections (state) {
      if (state.post.recentOrder) {
        return state.post.recentOrder.completedInspections
      } else {
        return null
      }
    }
  },
  actions: {
    countPostWords (context) {
      const params = {
        post: {
          items_attributes: activeItemsAttributesQuery(context)
        }
      }
      axios.post('/api/post_words_count', params).then(
        ({ data }) => {
          context.commit('SET_WORDS_COUNT', data.postWordsCount)
        },
        (error) => {
          console.error(error)
        }
      )
    },
    fetchOrderRegulationCheckResults (context, { orderId }) {
      axios.get(`/api/orders/${orderId}/regulation_check`).then(
        ({ data }) => {
          context.commit('SET_ORDER_REGULATION_CHECK_RESULTS', data.checkResult)
        },
        (error) => {
          console.error(error)
        }
      )
    },
    fetchPostRegulationCheckResults (context, { postId }) {
      axios.get(`/api/posts/${postId}/regulation_check`).then(
        ({ data }) => {
          context.commit('SET_POST_REGULATION_CHECK_RESULTS', data.checkResult)
        },
        (error) => {
          console.error(error)
        }
      )
    },
    // 一旦、ここを修正
    fetchPost (context, query) {
      return axios.get(`/api/posts/${query.postId}/edit`).then(
        (response) => {
          postResponseProcessing(context, response)
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.errors.toString()
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    fetchTargetPost ({ commit }, query) {
      axios.get(`/api/posts/${query.postId}`).then(
        ({ data }) => {
          const payload = {
            uuid: query.itemUuid,
            post: data.data.attributes
          }
          commit('SET_TARGET_POST', payload)
        },
        (error) => {
          console.log(error)
        }
      )
    },
    createPost (context) {
      const query = {
        post: {
          title: '',
          is_moral: true,
          is_index: true,
          is_pagination: true,
          is_pr: false,
          status: 'draft'
        }
      }
      axios.post('/api/posts', query).then(
        ({ data }) => {
          const postId = deserialize(data).data.id
          router.push({ name: 'adminPostEdit', params: { id: postId } })
          const payload = {
            status: 200,
            message: '新規投稿を作成しました。'
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.messages
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    async updatePost (context) {
      context.dispatch('LoaderStore/displayLoader', null, { root: true })
      try {
        const query = postUpdateQuery(context)
        const response = await axios.patch(`/api/posts/${context.state.post.id}`, query)
        postResponseProcessing(context, response)
        const payload = {
          status: 200,
          message: '変更を保存しました'
        }
        context.dispatch('SystemMessageStore/createMessage', payload, {
          root: true
        })
        return true
      } catch (error) {
        const response = error && error.response
        const data = response && response.data
        const payload = {
          status: (response && response.status) || 500,
          message: (data && data.message) ||
            '記事の保存処理を完了できませんでした。再度お試しください。'
        }
        context.dispatch('SystemMessageStore/createMessage', payload, {
          root: true
        })
        return false
      } finally {
        context.dispatch('LoaderStore/hideLoader', null, { root: true })
      }
    },
    deliverOrder (context, orderId) {
      context.dispatch('LoaderStore/displayLoader', null, { root: true })
      return axios.post(`/api/orders/${orderId}/delivery`).then(
        ({ data }) => {
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const order = data.data.attributes
          context.commit('SET_ORDER', { order })
          judgeEditorMode(context)
          const payload = {
            status: 200,
            message: '納品しました。検収が完了するまでお待ちください。'
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        },
        (error) => {
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const payload = {
            status: error.response.status,
            message: error.response.statusText
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    cancelOrder (context, orderId) {
      context.dispatch('LoaderStore/displayLoader', null, { root: true })
      axios.patch(`/api/orders/${orderId}/cancel`).then(
        () => {
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const payload = {
            status: 200,
            message: '執筆をキャンセルしました。'
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
          router.go({ name: 'posts', force: true })
        },
        (error) => {
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const payload = {
            status: error.response.status,
            message: error.response.statusText
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    async publishPost (context, updateReleasedOn = false) {
      context.dispatch('LoaderStore/displayLoader', null, { root: true })
      try {
        const statePost = context.state.post
        const query = postUpdateQuery(context, updateReleasedOn)
        const response = await axios.post(`/api/posts/${statePost.id}/publications`, query)
        setResponseDataToState(context.commit, response)
        const payload = {
          status: 200,
          message: '投稿を公開しました'
        }
        context.dispatch('SystemMessageStore/createMessage', payload, {
          root: true
        })
        return true
      } catch (error) {
        const response = error && error.response
        const data = response && response.data
        const payload = {
          status: (response && response.status) || 500,
          message: (data && data.messages) ||
            '記事の公開処理を完了できませんでした。再度お試しください。'
        }
        context.dispatch('SystemMessageStore/createMessage', payload, {
          root: true
        })
        return false
      } finally {
        context.dispatch('LoaderStore/hideLoader', null, { root: true })
      }
    },
    publishReservation (context, { reserveDatetime, updateReleasedOn }) {
      context.dispatch('LoaderStore/displayLoader', null, { root: true })
      const formattedReserveDatetime = new Date(reserveDatetime)
      const statePost = context.state.post
      const query = {
        publish_reservation: {
          post_id: statePost.id,
          reserved_at: formattedReserveDatetime,
          update_released_on: updateReleasedOn
        }
      }
      axios.post(`/api/publish_reservations`, query).then(
        ({ data }) => {
          context.commit('SET_PUBLISH_RESERVATION', deserialize(data).data)
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const payload = {
            status: 200,
            message: '投稿の公開予約が完了しました'
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        },
        (error) => {
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const payload = {
            status: error.response.status,
            message: error.response.data.messages
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    deletePublishReservation (context, id) {
      axios.delete(`/api/publish_reservations/${id}`).then(
        () => {
          context.commit('DELETE_PUBLISH_RESERVATION')
          const payload = {
            status: 200,
            message: '投稿の公開予約を取り消しました'
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.messages
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    closePost (context) {
      const statePost = context.state.post
      const query = postUpdateQuery(context)
      query.post.status = 'closed'

      axios.patch(`/api/posts/${statePost.id}`, query).then(
        (response) => {
          setResponseDataToState(context.commit, response)
          const payload = {
            status: 200,
            message: '投稿を非公開にしました'
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.message
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    startInspection (context, { inspectionId }) {
      context.dispatch('LoaderStore/displayLoader', null, { root: true })
      axios.post(`/api/inspections/${inspectionId}/start`).then(
        ({ data }) => {
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const inspection = data.data.attributes
          context.commit('SET_INSPECTION', { inspection })
          judgeEditorMode(context)
          const payload = {
            status: 200,
            message: '検収が開始されました'
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        },
        (error) => {
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const payload = {
            status: error.response.status,
            message: error.response.data.messages
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    overrideInspection (context, { inspectionId }) {
      context.dispatch('LoaderStore/displayLoader', null, { root: true })
      axios.post(`/api/inspections/${inspectionId}/override`).then(
        ({ data }) => {
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const inspection = data.data.attributes
          context.commit('SET_INSPECTION', { inspection })
          judgeEditorMode(context)
          const payload = {
            status: 200,
            message: '検収権限が上書きされました'
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        },
        (error) => {
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const payload = {
            status: error.response.status,
            message: error.response.data.messages
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    cancelInspection (context, { inspectionId }) {
      context.dispatch('LoaderStore/displayLoader', null, { root: true })
      axios.post(`/api/inspections/${inspectionId}/cancel`).then(
        ({ data }) => {
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const inspection = data.data.attributes
          context.commit('SET_INSPECTION', { inspection })
          judgeEditorMode(context)
          const payload = {
            status: 200,
            message: '検収がキャンセルされました'
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        },
        (error) => {
          context.dispatch('LoaderStore/hideLoader', null, { root: true })
          const payload = {
            status: error.response.status,
            message: error.response.data.messages
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    updateInspection (context, { inspectionId, inspectionAttributes }) {
      const query = { inspection: inspectionAttributes }
      axios.patch(`/api/inspections/${inspectionId}`, query).then(
        ({ data }) => {
          const inspection = data.data.attributes
          context.commit('SET_INSPECTION', { inspection })
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.messages
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    completeInspection (context, payload) {
      context.dispatch('LoaderStore/displayLoader', null, { root: true })
      const query = { result: payload.result }
      axios
        .post(
          `/api/inspections/${context.getters.currentActiveInspection.id}/complete`,
          query
        )
        .then(
          ({ data }) => {
            router.go({
              name: 'postEdit',
              params: { id: context.state.post.id },
              force: true
            })
            const inspection = data.data.attributes
            context.commit('SET_INSPECTION', { inspection })
            judgeEditorMode(context)
          },
          (error) => {
            context.dispatch('LoaderStore/hideLoader', null, { root: true })
            const payload = {
              status: error.response.status,
              message: error.response.data.messages
            }
            context.dispatch('SystemMessageStore/createMessage', payload, {
              root: true
            })
          }
        )
    },
    postItemComment (context, payload) {
      const query = {
        item_comment: {
          content: payload.content
        }
      }
      const { callback } = payload
      axios.post(`/api/items/${payload.item.id}/item_comments`, query).then(
        (response) => {
          const desirializedResponse = deserialize(response.data)
          context.commit('ADD_NEW_ITEM_COMMENT', {
            itemUuid: payload.item.uuid,
            itemComment: desirializedResponse.data
          })
          callback.call()

          context.dispatch(
            'SystemMessageStore/createMessage',
            {
              status: 200,
              message: 'コメントを作成しました。'
            },
            {
              root: true
            }
          )
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.message
          }
          context.dispatch('SystemMessageStore/createMessage', payload, {
            root: true
          })
        }
      )
    },
    updateItemComment (context, { itemCommentContent, item, itemCommentId }) {
      const query = {
        item_comment: {
          content: itemCommentContent
        }
      }
      axios
        .patch(`/api/items/${item.id}/item_comments/${itemCommentId}`, query)
        .then(
          ({ data }) => {
            const payload = {
              status: 200,
              message: '変更を保存しました'
            }
            context.dispatch('SystemMessageStore/createMessage', payload, {
              root: true
            })
            context.commit('UPDATE_ITEM_COMMENT', {
              itemComment: deserialize(data).data,
              itemUuid: item.uuid
            })
          },
          (error) => {
            const payload = {
              status: error.response.status,
              message: error.response.data.message
            }
            context.dispatch('SystemMessageStore/createMessage', payload, {
              root: true
            })
          }
        )
    },
    deleteItemComment (context, { item, itemComment }) {
      axios
        .delete(`/api/items/${item.id}/item_comments/${itemComment.id}`)
        .then(
          (response) => {
            context.commit('DELETE_ITEM_COMMENT', {
              itemUuid: item.uuid,
              itemCommentId: itemComment.id
            })
            const payload = {
              status: response.status,
              message: response.data.message
            }
            context.dispatch('SystemMessageStore/createMessage', payload, {
              root: true
            })
          },
          (error) => {
            const payload = {
              status: error.response.status,
              message: error.response.data.message
            }
            context.dispatch('SystemMessageStore/createMessage', payload, {
              root: true
            })
          }
        )
    }
  }
}

const isPremiumDisplayPost = (statePost) => {
  const itemsArray = statePost.items
  let hasAnyAffiliateLink = null

  const postType = statePost.postType

  const domainCheckList = ['amazon.co.jp', 'rakuten.co.jp', 'shopping.yahoo.co.jp', 'zozo.ne.jp']
  const containsAffiliateLink = (url) => {
    return url && domainCheckList.some(affiliate => url.includes(affiliate))
  }

  if (postType === 'product') {
    const hasAmazonAffiliateLink = (statePost.product.productLinks || []).some(link => containsAffiliateLink(link.url))
    if (hasAmazonAffiliateLink) {
      return true
    }
  }

  for (const item of itemsArray) {
    if (item.variety === 'product_introduction') {
      if (item.itemableAttributes.productId) {
        const hasAmazonAffiliateLink = (item.itemableAttributes.product.productLinks || []).some(link => containsAffiliateLink(link.url))
        if (hasAmazonAffiliateLink) {
          return true
        }
      }

      const { amazonUrl, rakutenUrl, otherUrlSentence, otherUrl } = item.itemableAttributes
      if (amazonUrl || rakutenUrl) {
        hasAnyAffiliateLink = true
        break
      }

      if ((otherUrlSentence && containsAffiliateLink(otherUrlSentence)) || (otherUrl && containsAffiliateLink(otherUrl))) {
        hasAnyAffiliateLink = true
        break
      }
    }
  }

  return hasAnyAffiliateLink
}

const judgeEditorMode = (context) => {
  const recentOrder = context.state.post.recentOrder
  const post = context.state.post
  const currentUser = context.rootState.UserStore.currentUser
  let mode
  if (recentOrder === undefined) {
    mode = 'edit'
  } else {
    const inspection = context.state.post.recentOrder.currentActiveInspection
    if (
      recentOrder.status === 'writing' &&
      recentOrder.userId === post.userId &&
      recentOrder.userId === currentUser.id
    ) {
      mode = 'edit'
    } else if (
      inspection &&
      recentOrder.status === 'under_inspection' &&
      inspection.inspectorId === currentUser.id
    ) {
      mode = 'inspection'
    } else if (
      currentUser.isAdmin &&
      (recentOrder.status === 'completed' ||
        recentOrder.status === 'client_completed' ||
        recentOrder.status === 'not_applied' ||
        recentOrder.status === 'writing' ||
        recentOrder.status === 'under_detection')
    ) {
      mode = 'edit'
    } else {
      mode = 'readonly'
    }
  }

  context.commit('SET_MODE', { mode })
}

const postResponseProcessing = (context, response) => {
  setResponseDataToState(context.commit, response)
  judgeEditorMode(context)
  context.dispatch('countPostWords')
  const recentOrder = context.getters.recentOrder
  const post = context.state.post
  if (recentOrder && recentOrder.regulationBundlerId) {
    context.dispatch('fetchOrderRegulationCheckResults', {
      orderId: recentOrder.id
    })
  } else if (post.regulationBundlerId) {
    context.commit('SET_SAVED_REGULATION_BUNDLER_ID', true)
    context.dispatch('fetchPostRegulationCheckResults', {
      postId: post.id
    })
  }
}

const setResponseDataToState = (commit, { data }) => {
  const desirializedResponse = deserialize(data)
  let post = desirializedResponse.data
  if (post.items) {
    post.items.forEach((item) => {
      const additionalAttributes = {
        uuid: uuidv4(),
        _destroy: false,
        newItem: false,
        isSaved: true
      }
      Object.assign(item, additionalAttributes)
    })
  } else {
    Object.assign(post, { items: [] })
  }
  commit('SET_POST', { post })
  commit('SET_POST_SAVED_TO_DB', true)
}

const postUpdateQuery = (context, updateReleasedOn = false) => {
  const statePost = context.state.post
  const littleTagsId =
    statePost.littleTags && statePost.littleTags.length !== 0
      ? statePost.littleTags.map((tag) => tag.id)
      : []
  const categoryId = statePost.category ? statePost.category.id : null
  const productId = statePost.product ? statePost.product.id : null

  return {
    post: {
      title: statePost.title,
      meta_title: statePost.metaTitle,
      introduction: statePost.introduction,
      is_pagination: statePost.isPagination,
      is_index: statePost.isIndex,
      is_moral: statePost.isMoral,
      is_pr: statePost.isPr,
      is_premiumDisplay: statePost.isPremiumDisplay !== null ? statePost.isPremiumDisplay : isPremiumDisplayPost(statePost),
      is_table_of_contents: statePost.isTableOfContents,
      eyecatchable_id: statePost.eyecatchableId,
      eyecatchable_type: statePost.eyecatchableType,
      eyecatch_source_url: statePost.eyecatchSourceUrl,
      expertable_id: statePost.expertableId,
      expertable_type: statePost.expertableType,
      items_attributes: itemsAttributesQuery(context),
      writerable_id: statePost.writerableId,
      writerable_type: statePost.writerableType,
      little_tag_ids: littleTagsId,
      image_direct_url: statePost.imageDirectUrl,
      slug: statePost.slug,
      post_type: statePost.postType,
      product_id: productId,
      update_released_on: updateReleasedOn,
      regulation_bundler_id: statePost.regulationBundlerId,
      rank_keywords: statePost.rankKeywords,
      display_widget: statePost.displayWidget
    },
    category_id: categoryId
  }
}

const formattedItemQuery = (originalItem) => {
  let item = Object.assign({}, originalItem)
  delete item.uuid
  delete item.newItem
  delete item.isSaved
  delete item.type
  Object.assign(item, {
    itemable_attributes: replaceTextLineBrakeCode(item)
  })
  delete item.itemableAttributes

  return item
}

const replaceTextLineBrakeCode = (item) => {
  let itemAttributes = cloneDeep(item.itemableAttributes)
  if (item.variety === 'text') {
    itemAttributes['content'] = itemAttributes['content'].replace(
      /\r?\n?/g,
      ''
    )
  }
  if (item.variety === 'product_introduction' && itemAttributes.tab == 1) {
    Object.assign(itemAttributes, {
      alternateName: null,
      amazonUrl: null,
      description: null,
      downloadImageUrl: null,
      images: null,
      name: null,
      officialUrl: null,
      otherUrl: null,
      otherUrlSentence: null,
      price: null,
      rakutenUrl: null
    })
  } else if (item.variety === 'product_introduction' && itemAttributes.tab == 0) {
    itemAttributes.productId = null
  }
  return itemAttributes
}

const itemsAttributesQuery = (context) => {
  const activeItems = [...context.getters.activeItems]
  const deletedItems = [...context.getters.deletedItems]
  const itemsAttributes = []

  activeItems.forEach((originalItem, index) => {
    let item = formattedItemQuery(originalItem)
    Object.assign(item, { order: index })
    itemsAttributes.push(item)
  })
  deletedItems.forEach((originalItem) => {
    let item = formattedItemQuery(originalItem)
    itemsAttributes.push(item)
  })

  return itemsAttributes
}

const activeItemsAttributesQuery = (context) => {
  const activeItems = [...context.getters.activeItems]
  const itemsAttributes = []

  activeItems.forEach((originalItem, index) => {
    let item = formattedItemQuery(originalItem)
    delete item._destroy
    Object.assign(item, { order: index })
    itemsAttributes.push(item)
  })

  return itemsAttributes
}

export default PostEditor
